import React, { useEffect, useState, useCallback, Fragment } from 'react';
import {
  Badge,
  Box,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
} from '@material-ui/core';
import { ExpandMore, Close } from '@material-ui/icons';
import { AxiosRequestConfig } from 'axios';
import instance from 'api';
import { IAvailableColumnModel, IDictionaryMap, IFilterColumnModel } from 'interfaces';
import DatePicker, { DateRangePicker, DateTimePicker } from 'components/DatePicker';
import AsyncSearch from 'components/AsyncSearch';
import TextField from 'components/TextField';

interface IDictionaryOption {
  key: any;
  label: string;
}

interface IDictionaryFilterProps {
  filter: IAvailableColumnModel;
  // Отдельным пропом, а не из filter: там dictionaryMap nullable, а без словаря этому фильтру
  // нечем жить. Не-nullable проп заставляет вызывающую сторону отсеять null до рендера —
  // ошибку ловит компилятор, а не прод.
  dictionaryMap: IDictionaryMap;
  initialValue: any[];
  onChange: (filter: IAvailableColumnModel, data: any[]) => void;
}

const SEARCH_PARAM = 'search';

// Справочник строк приходит массивом значений, остальные — объектами с ключом и подписью
const toOption = (raw: any, dictionaryMap: IDictionaryMap): IDictionaryOption =>
  dictionaryMap.keyColumnName === null
    ? { key: raw, label: String(raw) }
    : { key: raw[dictionaryMap.keyColumnName], label: String(raw[dictionaryMap.valueColumnName!] ?? '') };

// Отсутствующий фильтр представлен пустой строкой, пикерам дат вместо неё уходит null
const toPickerValue = (value: any) => (value === '' ? null : value);

const DictionaryFilter: React.FC<IDictionaryFilterProps> = ({ filter, dictionaryMap, initialValue, onChange }) => {
  const searchColumns = dictionaryMap.searchColumns || [];
  // Поиск на бэкенде есть не у всех справочников: без него список приходит целиком
  const searchColumn = searchColumns.length ? searchColumns[0] : dictionaryMap.searchColumn;

  const values: IDictionaryOption[] = initialValue.map((item) =>
    item.key === undefined ? toOption(item, dictionaryMap) : { key: item.key, label: String(item.value) },
  );

  const fetchOptions = (config?: AxiosRequestConfig): Promise<IDictionaryOption[]> => {
    const search = String(config?.params?.[SEARCH_PARAM] ?? '').trim();
    const query = searchColumn && search ? { [searchColumn]: search } : {};

    // FIXME: Временное решение
    const isBaseApi = !dictionaryMap.dictionaryUrl.match(/^\/api/);
    const request = isBaseApi
      ? instance().post(dictionaryMap.dictionaryUrl, query)
      : instance(true, '').get(dictionaryMap.dictionaryUrl, { params: query });

    return request.then((response) => response.data.map((raw: any) => toOption(raw, dictionaryMap)));
  };

  return (
    <AsyncSearch<IDictionaryOption, true>
      multiple
      disableCloseOnSelect
      limitTags={1}
      fetch={fetchOptions}
      remoteSearch={Boolean(searchColumn)}
      searchParamName={SEARCH_PARAM}
      uniqueBy="key"
      matchKeys={['label']}
      value={values}
      getOptionLabel={(option) => option.label}
      renderOption={(option, { selected }) => (
        <>
          <Checkbox color="primary" checked={selected} />
          {option.label}
        </>
      )}
      renderInput={(params) => <TextField {...params} fullWidth autoComplete="off" label={filter.filterCaption} />}
      onChange={(_, items) =>
        onChange(
          filter,
          (items as IDictionaryOption[]).map((option) => ({ key: option.key, value: option.label })),
        )
      }
    />
  );
};

interface IFiltersProps {
  filters: IAvailableColumnModel[];
  filterColumns: IFilterColumnModel[];
  hideFilters?: string[];
  expanded: boolean;
  baseApiUrl?: string;
  className: string;
  setFilterColumns: (filterColumns: IFilterColumnModel[]) => void;
  doFilter: () => void;
  setExpanded: (bool: boolean) => void;
}

const ExpandableFilters: React.FC<IFiltersProps> = (props) => {
  const {
    filters,
    expanded,
    setExpanded,
    filterColumns: propsFilterColumns,
    setFilterColumns: propsSetFilterColumns,
    hideFilters = [],
  } = props;
  const [filterColumns, setFilterColumns] = useState<IFilterColumnModel[]>(props.filterColumns);

  const propsFilterColumnsChange = useCallback(() => {
    if (propsFilterColumns.length === 0) {
      setFilterColumns([]);
      propsSetFilterColumns([]);
    }
  }, [propsFilterColumns, propsSetFilterColumns]);

  useEffect(propsFilterColumnsChange, [props.filterColumns]);

  const setFilterColumn = (filter: IAvailableColumnModel, value: any[]): IFilterColumnModel[] => {
    if (value.length > 0) {
      if (filterColumns.length < 1) {
        return [
          {
            columnName: filter.columnName,
            criteria: filter.filterCriteriaList[0],
            filterValues: value,
          },
        ];
      } else {
        const filterItem = filterColumns.filter((filterColumn) => filterColumn.columnName === filter.columnName)[0];

        if (filterItem) {
          return filterColumns.map((filterColumn) => {
            if (filterColumn.columnName === filter.columnName) {
              return {
                ...filterColumn,
                filterValues: value,
              };
            }

            return { ...filterColumn };
          });
        } else {
          return [
            ...filterColumns,
            {
              columnName: filter.columnName,
              criteria: filter.filterCriteriaList[0],
              filterValues: value,
            },
          ];
        }
      }
    } else {
      return [...filterColumns].filter((filterColumn) => filterColumn.columnName !== filter.columnName);
    }
  };

  const onChangeDateRange = (filter: IAvailableColumnModel) => (value: string) => {
    if (value === '') {
      onClickClear(filter)();
    } else {
      const newFilterColumns = setFilterColumn(filter, [value]);

      setFilterColumns(newFilterColumns);
      props.setFilterColumns(newFilterColumns);
    }
  };

  const onChangeStringType = (filter: IAvailableColumnModel) => (e: any) => {
    if (e.target.value === '') {
      onClickClear(filter)();
    } else {
      const newFilterColumns = setFilterColumn(filter, [e.target.value]);

      setFilterColumns(newFilterColumns);
      props.setFilterColumns(newFilterColumns);
    }
  };

  const onChangeIntegerType = (filter: IAvailableColumnModel) => (e: any) => {
    e.persist();
    const { value } = e.target;

    if (value === '') {
      onClickClear(filter)();
    } else {
      const newFilterColumns = setFilterColumn(filter, [value ? value.replace(/[^0-9]/g, '') : '']);

      setFilterColumns(newFilterColumns);
      props.setFilterColumns(newFilterColumns);
    }
  };

  const onChangeBooleanType = (filter: IAvailableColumnModel) => (e: any) => {
    if (e.target.value !== '') {
      const newFilterColumns = setFilterColumn(filter, [e.target.value.valueOf() === 'true'.valueOf()]);

      setFilterColumns(newFilterColumns);
      props.setFilterColumns(newFilterColumns);
    }
  };

  const onChangeYearMonth = (filter: IAvailableColumnModel) => (date: Date) => {
    const newFilterColumns = setFilterColumn(filter, [date]);

    setFilterColumns(newFilterColumns);
    props.setFilterColumns(newFilterColumns);
  };

  const onChangeDateTime = (filter: IAvailableColumnModel) => (date: Date) => {
    const newFilterColumns = setFilterColumn(filter, [date]);

    setFilterColumns(newFilterColumns);
    props.setFilterColumns(newFilterColumns);
  };

  const onClickClear = (filter: IAvailableColumnModel) => () => {
    const newFilterColumns = filterColumns.filter((filterColumn) => filterColumn.columnName !== filter.columnName);

    setFilterColumns(newFilterColumns);
    props.setFilterColumns(newFilterColumns);
  };

  const onChangeSelectArrayType = (filter: IAvailableColumnModel, data: any[]) => {
    const newFilterColumns = setFilterColumn(filter, data);
    setFilterColumns(newFilterColumns);
    props.setFilterColumns(newFilterColumns);
  };

  useEffect(() => setFilterColumns(props.filterColumns), [props.filterColumns]);

  const renderFilterItem = (filter: IAvailableColumnModel): React.ReactNode => {
    const filterItem = filterColumns.filter((filterColumn) => filterColumn.columnName === filter.columnName)[0];
    let value: any = '';

    if (filterItem) value = filterItem.filterValues;

    // Выпадашка — только там, где её есть чем наполнить. Колонку с criteria IN/ARRAY_OVERLAP,
    // но без DictionaryURL бэкенд отдаёт с dictionaryMap: null — такая уходит дальше, в обычное
    // поле по типу колонки. Критерий при этом сохраняется: setFilterColumn берёт
    // filterCriteriaList[0].
    if (filter.dictionaryMap !== null) {
      return (
        <Grid item style={{ maxWidth: '20%', flexBasis: '20%' }} onClick={() => setExpanded(true)}>
          <DictionaryFilter
            key={filter.columnName}
            onChange={onChangeSelectArrayType}
            initialValue={!!value ? value : []}
            filter={filter}
            dictionaryMap={filter.dictionaryMap}
          />
        </Grid>
      );
    }

    if (filter.filterCriteriaList.indexOf('BETWEEN') > -1) {
      return (
        <Grid item style={{ maxWidth: '20%', flexBasis: '20%' }} onClick={() => setExpanded(true)}>
          <DateRangePicker
            label={filter.caption}
            onClickClear={onClickClear(filter)}
            onChange={onChangeDateRange(filter)}
            value={value[0] || ''}
          />
        </Grid>
      );
    }

    if (filter.columnName === 'yearMonth') {
      return (
        <Grid item style={{ maxWidth: '20%', flexBasis: '20%' }} onClick={() => setExpanded(true)}>
          <DatePicker
            label={filter.caption}
            format="MMM yyyy"
            openTo="year"
            views={['year', 'month']}
            onClear={onClickClear(filter)}
            value={toPickerValue(value)}
            fullWidth
            onChange={onChangeYearMonth(filter)}
          />
        </Grid>
      );
    }

    switch (filter.type) {
      case 'Integer':
        return (
          <Grid item style={{ maxWidth: '20%', flexBasis: '20%' }} onClick={() => setExpanded(true)}>
            <TextField
              label={filter.caption}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              value={value}
              fullWidth
              clear
              onClickClear={onClickClear(filter)}
              onChange={onChangeIntegerType(filter)}
            />
          </Grid>
        );
      case 'Timestamp':
        return (
          <Grid item style={{ maxWidth: '20%', flexBasis: '20%' }} onClick={() => setExpanded(true)}>
            <DateTimePicker
              label={filter.caption}
              format="dd.MM.yyyy HH:mm"
              onClear={onClickClear(filter)}
              ampm={false}
              value={toPickerValue(value)}
              fullWidth
              onChange={onChangeDateTime(filter)}
            />
          </Grid>
        );
      case 'String':
        return (
          <Grid item style={{ maxWidth: '20%', flexBasis: '20%' }} onClick={() => setExpanded(true)}>
            <TextField
              label={filter.caption}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  props.doFilter();
                }
              }}
              value={value}
              fullWidth
              clear
              onClickClear={onClickClear(filter)}
              onChange={onChangeStringType(filter)}
            />
          </Grid>
        );
      case 'Boolean':
        return (
          <Grid item style={{ maxWidth: '20%', flexBasis: '20%' }} onClick={() => setExpanded(true)}>
            <FormControl variant="filled" fullWidth>
              <InputLabel>{filter.caption}</InputLabel>

              {/* onChangeBooleanType кладёт в filterValues массив ([true]/[false]), а Select ждёт
                  значение пункта меню строкой — без разворота выбранный фильтр показывал бы пустое
                  поле, в том числе фильтр, выставленный страницей по умолчанию. */}
              <Tooltip title={filter.caption.length > 20 ? filter.caption : ''} placement="top">
                <Select
                  value={Array.isArray(value) ? String(value[0]) : value}
                  endAdornment={
                    <InputAdornment position="end" style={{ paddingRight: 18 }}>
                      <IconButton size="small" onClick={onClickClear(filter)}>
                        <Close style={{ opacity: 0.5 }} />
                      </IconButton>
                    </InputAdornment>
                  }
                  onChange={onChangeBooleanType(filter)}
                >
                  <MenuItem value="true">Да</MenuItem>
                  <MenuItem value="false">Нет</MenuItem>
                </Select>
              </Tooltip>
            </FormControl>
          </Grid>
        );
      case 'Date':
        return (
          <Grid item style={{ maxWidth: '20%', flexBasis: '20%' }} onClick={() => setExpanded(true)}>
            <DatePicker
              label={filter.caption}
              format="dd.MM.yyyy"
              onClear={onClickClear(filter)}
              value={toPickerValue(value)}
              fullWidth
              onChange={onChangeDateTime(filter)}
            />
          </Grid>
        );
      case 'BigDecimal':
        return null;
      default:
        return null;
    }
  };

  return (
    <>
      {Boolean(props.filters.length) && (
        <Paper
          style={{
            position: 'absolute',
            maxHeight: expanded ? '' : 74,
            width: 'calc(100% - 16px)',
            zIndex: 1200,
            overflow: expanded ? 'visible' : 'hidden',
          }}
          className={props.className}
        >
          <Box p={1}>
            <Grid container alignItems="flex-start" wrap="nowrap" spacing={1}>
              <Grid item container spacing={1}>
                {filters
                  .filter((item) => !!item)
                  .map((filter: IAvailableColumnModel) => {
                    if (hideFilters.includes(filter.columnName)) return <Fragment key={filter.columnName} />;

                    return <Fragment key={filter.columnName}>{renderFilterItem(filter)}</Fragment>;
                  })}
              </Grid>

              {Boolean(props.filters.length > 5) && (
                <Grid item>
                  <IconButton
                    style={{ backgroundColor: '#044bb5', color: '#fff' }}
                    onClick={() => setExpanded(!expanded)}
                  >
                    <Badge badgeContent={props.filters.length > 5 ? props.filters.length - 5 : 0} color="secondary">
                      <ExpandMore
                        style={{
                          transform: expanded ? 'rotate(-180deg)' : 'rotate(0deg)',
                          transition: 'all 0.4s ease',
                        }}
                      />
                    </Badge>
                  </IconButton>
                </Grid>
              )}
            </Grid>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default ExpandableFilters;
